import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ScreenOptionsEnum from "@models/ScreenOptionsEnum";
import { FilterValue } from "@models/IFilterPresets";
import { FilterValues, IFilterField, IFilterSection } from "@models/IFilterConfig";
import { IFilterPresetRuntime } from "@models/IFilterPresetsUI";
import {
    areFiltersEqual,
    buildApiQuery,
    countActive,
    countNonExclusionFilters,
    getAllFields,
    getDefaultValue,
    isFieldActive,
    newPresetId,
    normalizeFilters,
    pickActive,
    reorderPresetsWithin,
} from "./filterBarUtils";

export type FilterBarMode = "inline" | "drawer" | "auto";

export interface FilterBarProps {
    // Required
    /** Filter config for this screen */
    sections: IFilterSection[];
    /** Called with the API-ready query object */
    onSearch: (query: { [apiKey: string]: unknown }) => void;
    /** Which screen — drives preset storage key and "All [Screen]" tab label */
    screenKey: ScreenOptionsEnum;

    // Presets (optional — omit for screens that don't need presets)
    /** Merged team + user presets (from mergePresets()) */
    presets?: IFilterPresetRuntime[];
    /** Called on save/update/delete with the full updated array */
    onPresetsChange?: (presets: IFilterPresetRuntime[]) => void;

    // Optional overrides
    /** Default: "auto" (based on filter count) */
    mode?: FilterBarMode;
    /** Baseline filter values: used on mount (if no default preset) and restored by "All", Reset and Clear all */
    defaultFilters?: FilterValues;
    /** Override the first tab label (default derived from screenKey, e.g. "all_orders") */
    allLabel?: string;

    // Additions beyond the spec
    /**
     * Deep-link values (keyed by field key). When non-empty they win over the default preset on mount.
     * The active tab is the preset whose filters match them (default preset first), else "All".
     */
    initialFilters?: FilterValues;
    /** DOM id of a title-row element; when set, the Refresh + Filters buttons are portaled into it (drawer mode only). */
    actionsContainerId?: string;
    /** When true the Search/Refresh buttons show a spinner and are disabled */
    isSearching?: boolean;
    /**
     * When provided, the drawer footer shows a "Send a suggestion" link. FilterBar prepends the screen
     * name and the active filters to the user's text before calling this.
     */
    onSendFeedback?: (message: string) => Promise<void> | void;
}

export interface ContextMenuState {
    /** null = the "All [Screen]" tab */
    presetId: string | null;
    x: number;
    y: number;
}

export type OptionLabels = { [fieldKey: string]: { [value: string]: string } };

function hasValues(values: FilterValues | undefined): values is FilterValues {
    return !!values && Object.keys(values).length > 0;
}

export function findDefaultPreset(presets: IFilterPresetRuntime[] | undefined): IFilterPresetRuntime | undefined {
    if (!presets) {
        return undefined;
    }

    // A user's own default wins over a team default (a non-admin cannot clear the team flag).
    return presets.find((p) => p.isDefault && p.source === "user") || presets.find((p) => p.isDefault);
}

/**
 * The preset whose filters equal `values`, preferring the default preset when several match.
 * Used on mount to map URL filters back to a tab: the wrappers write the applied filters into the URL,
 * so a refresh (or Back / Forward) on a preset's view must land on that preset, not on "All".
 */
export function findMatchingPreset(
    values: FilterValues,
    presets: IFilterPresetRuntime[] | undefined,
    sections: IFilterSection[]
): IFilterPresetRuntime | undefined {
    if (!presets) {
        return undefined;
    }

    const defaultPreset = findDefaultPreset(presets);

    if (defaultPreset && areFiltersEqual(values, defaultPreset.filters, sections)) {
        return defaultPreset;
    }

    return presets.find((p) => areFiltersEqual(values, p.filters, sections));
}

/**
 * All FilterBar state and handlers. No JSX.
 *
 * - `applied` is what the last search used: it drives the chips, the badge count and Refresh.
 * - `draft` holds drawer edits; Search commits it to `applied`. Closing the drawer discards it.
 * - Chip removal, Clear all, Reset and preset clicks apply immediately and run the search.
 */
export function useFilterBar(props: FilterBarProps) {
    const { sections, presets, onPresetsChange, defaultFilters, initialFilters } = props;

    // Latest onSearch / sections for the search handlers, without making them re-create on every render.
    // Synced in an effect (not during render) and declared before the mount-search effect below so the
    // mount search sees the initial values and later actions see committed props.
    const onSearchRef = useRef(props.onSearch);
    const sectionsRef = useRef(sections);

    useEffect(() => {
        onSearchRef.current = props.onSearch;
        sectionsRef.current = sections;
    }, [props.onSearch, sections]);

    const baseline = useMemo(() => normalizeFilters(defaultFilters, sections), [defaultFilters, sections]);

    const [initial] = useState(() => {
        const defaultPreset = findDefaultPreset(presets);

        if (hasValues(initialFilters)) {
            const values = normalizeFilters(initialFilters, sections);
            const matched = findMatchingPreset(values, presets, sections);

            return { values, presetId: (matched ? matched.id : null) as string | null };
        }

        if (defaultPreset) {
            return { values: normalizeFilters(defaultPreset.filters, sections), presetId: defaultPreset.id };
        }

        return { values: normalizeFilters(defaultFilters, sections), presetId: null as string | null };
    });

    const [applied, setApplied] = useState<FilterValues>(initial.values);
    const [draft, setDraft] = useState<FilterValues>(initial.values);
    const [activePresetId, setActivePresetId] = useState<string | null>(initial.presetId);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [nameModalOpen, setNameModalOpen] = useState(false);
    const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
    const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
    const [optionLabels, setOptionLabels] = useState<OptionLabels>({});
    const [feedbackOpen, setFeedbackOpen] = useState(false);
    const [manageOpen, setManageOpen] = useState(false);

    const allFields = useMemo(() => getAllFields(sections), [sections]);

    const resolvedMode: "inline" | "drawer" = useMemo(() => {
        const mode = props.mode || "auto";

        if (mode !== "auto") {
            return mode;
        }

        return countNonExclusionFilters(sections) <= 3 ? "inline" : "drawer";
    }, [props.mode, sections]);

    const activePreset = useMemo(
        () => (activePresetId ? presets?.find((p) => p.id === activePresetId) : undefined),
        [activePresetId, presets]
    );

    const presetBaseline = useMemo(
        () => (activePreset ? normalizeFilters(activePreset.filters, sections) : baseline),
        [activePreset, baseline, sections]
    );

    const isModified = useMemo(() => !areFiltersEqual(applied, presetBaseline, sections), [applied, presetBaseline, sections]);
    const activeCount = useMemo(() => countActive(applied, sections), [applied, sections]);
    const draftActiveCount = useMemo(() => countActive(draft, sections), [draft, sections]);

    const runSearch = useCallback((values: FilterValues) => {
        onSearchRef.current(buildApiQuery(values, sectionsRef.current));
    }, []);

    // ── Mount: run the initial search exactly once (StrictMode-safe) ──
    const didMountSearch = useRef(false);

    useEffect(() => {
        if (didMountSearch.current) {
            return;
        }

        didMountSearch.current = true;
        runSearch(initial.values);
    }, [initial.values, runSearch]);

    // ── Sections changing (dynamic options arriving): re-normalize without wiping values ──
    useEffect(() => {
        setApplied((current) => {
            const next = normalizeFilters(current, sections);
            return areFiltersEqual(current, next, sections) && Object.keys(current).length === Object.keys(next).length ? current : next;
        });
        setDraft((current) => {
            const next = normalizeFilters(current, sections);
            return areFiltersEqual(current, next, sections) && Object.keys(current).length === Object.keys(next).length ? current : next;
        });
    }, [sections]);

    // ── Typeahead / category labels: resolve stored values that have no cached label (presets, deep links) ──
    useEffect(() => {
        let cancelled = false;

        for (const field of allFields) {
            if ((field.type !== "typeahead" && field.type !== "category") || !field.resolveOption) {
                continue;
            }

            const value = applied[field.key];
            const values = Array.isArray(value) ? value : isFieldActive(field, value) ? [String(value)] : [];

            for (const v of values) {
                if (optionLabels[field.key]?.[v]) {
                    continue;
                }

                field
                    .resolveOption(v)
                    .then((option) => {
                        if (!cancelled && option) {
                            setOptionLabels((current) => ({
                                ...current,
                                [field.key]: { ...(current[field.key] || {}), [v]: option.label },
                            }));
                        }
                    })
                    .catch(() => {
                        /* leave the raw value as the chip label */
                    });
            }
        }

        return () => {
            cancelled = true;
        };
        // optionLabels intentionally excluded: resolving updates it and must not re-trigger the loop
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applied, allFields]);

    const registerOptionLabel = useCallback((fieldKey: string, value: string, label: string) => {
        setOptionLabels((current) => {
            if (current[fieldKey]?.[value] === label) {
                return current;
            }

            return { ...current, [fieldKey]: { ...(current[fieldKey] || {}), [value]: label } };
        });
    }, []);

    // ── Drawer ──
    const openDrawer = useCallback(() => {
        setDraft(applied);
        setDrawerOpen(true);
    }, [applied]);

    const closeDrawer = useCallback(() => {
        setDrawerOpen(false);
        setDraft(applied);
    }, [applied]);

    const setDraftValue = useCallback((key: string, value: FilterValue) => {
        setDraft((current) => ({ ...current, [key]: value }));
    }, []);

    const applyAndSearch = useCallback(
        (values: FilterValues) => {
            setApplied(values);
            setDraft(values);
            runSearch(values);
        },
        [runSearch]
    );

    /** Drawer/inline Search button and Enter key: commit the draft and run the search. */
    const search = useCallback(() => {
        applyAndSearch(draft);
        setDrawerOpen(false);
    }, [applyAndSearch, draft]);

    /** Reset: back to the baseline of the current tab context ("All" baseline = defaultFilters). */
    const reset = useCallback(() => {
        setActivePresetId(null);
        applyAndSearch(baseline);
        setDrawerOpen(false);
    }, [applyAndSearch, baseline]);

    /** Refresh: re-run the current applied query without changing anything. */
    const refresh = useCallback(() => {
        runSearch(applied);
    }, [applied, runSearch]);

    const removeChip = useCallback(
        (field: IFilterField) => {
            applyAndSearch({ ...applied, [field.key]: getDefaultValue(field) });
        },
        [applied, applyAndSearch]
    );

    /** Clear all: resets to "All [Screen]" with the baseline filters. */
    const clearAll = useCallback(() => {
        setActivePresetId(null);
        applyAndSearch(baseline);
    }, [applyAndSearch, baseline]);

    // ── Presets ──
    const selectPreset = useCallback(
        (preset: IFilterPresetRuntime | null) => {
            setContextMenu(null);

            if (!preset) {
                setActivePresetId(null);
                applyAndSearch(baseline);
                return;
            }

            setActivePresetId(preset.id);
            applyAndSearch(normalizeFilters(preset.filters, sections));
        },
        [applyAndSearch, baseline, sections]
    );

    const emitPresets = useCallback(
        (next: IFilterPresetRuntime[]) => {
            if (onPresetsChange) {
                onPresetsChange(next);
            }
        },
        [onPresetsChange]
    );

    /** "+" tab: name the current applied filters. Footer "Save as New": commit the draft first. */
    const openSaveAsNew = useCallback(
        (fromDrawer?: boolean) => {
            if (fromDrawer) {
                applyAndSearch(draft);
                setDrawerOpen(false);
            }

            setNameModalOpen(true);
        },
        [applyAndSearch, draft]
    );

    const saveAsNew = useCallback(
        (name: string) => {
            const trimmed = name.trim();

            setNameModalOpen(false);

            if (!trimmed.length) {
                return;
            }

            const preset: IFilterPresetRuntime = {
                id: newPresetId(),
                name: trimmed,
                filters: pickActive(applied, sections),
                source: "user",
                editable: true,
            };

            setActivePresetId(preset.id);
            emitPresets([...(presets || []), preset]);
        },
        [applied, emitPresets, presets, sections]
    );

    /** Overwrite the active preset with the current draft (drawer) and run the search. */
    const updatePreset = useCallback(() => {
        if (!activePreset || !activePreset.editable) {
            return;
        }

        const values = drawerOpen ? draft : applied;

        applyAndSearch(values);
        setDrawerOpen(false);
        emitPresets((presets || []).map((p) => (p.id === activePreset.id ? { ...p, filters: pickActive(values, sections) } : p)));
    }, [activePreset, applied, applyAndSearch, draft, drawerOpen, emitPresets, presets, sections]);

    const requestDelete = useCallback((presetId: string) => {
        setContextMenu(null);
        setDeleteTargetId(presetId);
    }, []);

    /** Drawer footer "Delete preset": close the drawer (discarding the draft), then ask for confirmation. */
    const requestDeleteFromDrawer = useCallback(() => {
        if (!activePreset || !activePreset.editable) {
            return;
        }

        setDrawerOpen(false);
        setDraft(applied);
        setDeleteTargetId(activePreset.id);
    }, [activePreset, applied]);

    const confirmDelete = useCallback(() => {
        const target = deleteTargetId;

        setDeleteTargetId(null);

        if (!target) {
            return;
        }

        if (activePresetId === target) {
            setActivePresetId(null);
        }

        emitPresets((presets || []).filter((p) => p.id !== target));
    }, [activePresetId, deleteTargetId, emitPresets, presets]);

    const cancelDelete = useCallback(() => setDeleteTargetId(null), []);

    /**
     * Toggle isDefault on one preset. The API does not enforce a single default, so the UI clears
     * isDefault on every other preset of the same source (team and user lists are kept separately
     * because a non-admin cannot write the team list). On load a user default wins over a team default.
     */
    const toggleDefault = useCallback(
        (presetId: string) => {
            setContextMenu(null);

            const target = (presets || []).find((p) => p.id === presetId);

            if (!target) {
                return;
            }

            emitPresets(
                (presets || []).map((p) => {
                    if (p.id === presetId) {
                        return { ...p, isDefault: !p.isDefault };
                    }

                    return p.source === target.source ? { ...p, isDefault: false } : p;
                })
            );
        },
        [emitPresets, presets]
    );

    /**
     * "All [Screen]" as the default view: clear isDefault on every preset the user can edit
     * (own presets always; team presets when admin). A team default set by an admin still applies
     * to non-admins, who cannot clear it.
     */
    const setAllAsDefault = useCallback(() => {
        setContextMenu(null);
        emitPresets((presets || []).map((p) => (p.editable && p.isDefault ? { ...p, isDefault: false } : p)));
    }, [emitPresets, presets]);

    /** Whether no default preset applies, i.e. "All [Screen]" is the initial view. */
    const isAllDefault = !findDefaultPreset(presets);

    /** Right-click menu for a preset tab (editable presets only) or the "All" tab (`preset === null`). */
    const openContextMenu = useCallback((preset: IFilterPresetRuntime | null, x: number, y: number) => {
        if (preset && !preset.editable) {
            return;
        }

        setContextMenu({ presetId: preset ? preset.id : null, x, y });
    }, []);

    const closeContextMenu = useCallback(() => setContextMenu(null), []);

    // ── Manage sidebar: reorder within a source, rename, delete ──
    const openManage = useCallback(() => {
        setContextMenu(null);
        setManageOpen(true);
    }, []);

    const closeManage = useCallback(() => setManageOpen(false), []);

    /** New id order for one source's presets (from drag-and-drop). The other source is untouched. */
    const reorderPresets = useCallback(
        (source: "team" | "user", orderedIds: string[]) => {
            emitPresets(reorderPresetsWithin(presets || [], source, orderedIds));
        },
        [emitPresets, presets]
    );

    const renamePreset = useCallback(
        (presetId: string, name: string) => {
            const trimmed = name.trim();
            const target = (presets || []).find((p) => p.id === presetId);

            if (!target || !target.editable || !trimmed.length || trimmed === target.name) {
                return;
            }

            emitPresets((presets || []).map((p) => (p.id === presetId ? { ...p, name: trimmed } : p)));
        },
        [emitPresets, presets]
    );

    const canUpdatePreset =
        !!activePreset && activePreset.editable && (drawerOpen ? !areFiltersEqual(draft, presetBaseline, sections) : isModified);

    return {
        // state
        applied,
        draft,
        activePreset,
        activePresetId,
        drawerOpen,
        nameModalOpen,
        deleteTargetId,
        contextMenu,
        optionLabels,
        // derived
        allFields,
        resolvedMode,
        isModified,
        activeCount,
        draftActiveCount,
        canUpdatePreset,
        // handlers
        openDrawer,
        closeDrawer,
        setDraftValue,
        search,
        reset,
        refresh,
        removeChip,
        clearAll,
        selectPreset,
        openSaveAsNew,
        closeNameModal: () => setNameModalOpen(false),
        saveAsNew,
        updatePreset,
        requestDelete,
        requestDeleteFromDrawer,
        confirmDelete,
        cancelDelete,
        toggleDefault,
        setAllAsDefault,
        isAllDefault,
        openContextMenu,
        closeContextMenu,
        registerOptionLabel,
        manageOpen,
        openManage,
        closeManage,
        reorderPresets,
        renamePreset,
        feedbackOpen,
        openFeedback: () => setFeedbackOpen(true),
        closeFeedback: () => setFeedbackOpen(false),
    };
}

export type FilterBarController = ReturnType<typeof useFilterBar>;
